// src/components/Calendar/Calendar.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Menu from '../Menu/Menu';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Modal from 'react-modal';
import { Box } from '@mui/material';
import api from '../../utils/axios.secure'; // cookie-based axios (withCredentials:true)

// ❌ CSS imports removed intentionally (user project doesn't need them)

const customStyles = {
  content: {
    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
    marginRight: '-50%', transform: 'translate(-50%, -50%)', zIndex: 1000,
  },
  overlay: { zIndex: 999 },
};
Modal.setAppElement('#root');

const INACTIVITY_MS = 10 * 60 * 1000; // 10분
const GRACE_MS = 2 * 60 * 1000;       // 2분

const safeArrLen = (content) => {
  try {
    if (Array.isArray(content)) return content.length;
    if (typeof content === 'string') return JSON.parse(content || '[]').length;
    return 0;
  } catch { return 0; }
};

const fmtDuration = (sec) => {
  if (sec == null) return '—';
  const s = Number(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = Math.floor(s % 60);
  return h ? `${h}시간 ${m}분 ${r}초` : `${m}분 ${r}초`;
};

// playtime/updated_at 기반 세션 종료 추정
const computeStatus = (row) => {
  const totalSteps = safeArrLen(row.content);
  const current = Number(row.current ?? 0);
  const completed = totalSteps > 0 && current >= totalSteps;
  const started = current > 0;

  let sessionEnded = false;
  if (row.updated_at) {
    sessionEnded = (Date.now() - new Date(row.updated_at).getTime()) > INACTIVITY_MS;
  } else if (row.playtime != null && row.date) {
    const end = new Date(new Date(row.date).getTime() + Number(row.playtime) * 1000);
    sessionEnded = (Date.now() - end.getTime()) > GRACE_MS;
  }

  const dropout = started && !completed && sessionEnded;
  return { totalSteps, current, completed, dropout };
};

function Calendar() {
  const calendarRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventTitle, setEventTitle] = useState('');
  const [selectedEvent, setSelectedEvent] = useState({});
  const [events, setEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // 목록 가져오기 (서버가 JWT→clinic_id 매핑. body 없이 호출)
  const fetchEventsFromServer = useCallback(async () => {
  try {
    const { data } = await api.post('/manager/content/selectSche');

    // ✅ 서버 응답 확정 형태: { messageCode, messageString, data: { status, title, detail, instance, data: [...] } }
    const rows = Array.isArray(data?.data?.data) ? data.data.data : [];
    // console.log('[Calendar] parsed rows:', rows.length, rows);

    const fetched = rows.map((ev) => {
      const totalSteps = (() => {
        try {
          const c = ev.content;
          if (Array.isArray(c)) return c.length;
          if (typeof c === 'string') return JSON.parse(c || '[]').length;
          return 0;
        } catch { return 0; }
      })();

      const current = Number(ev.current ?? 0);
      const completed = totalSteps > 0 && current >= totalSteps;

      return {
        id: String(ev.idx),                   // ✅ 고유키 확정
        title: String(ev.user_id || '—'),
        start: ev.date ? new Date(ev.date) : undefined,
        allDay: true,
        backgroundColor: completed ? '#63C174' : '#64A8D2',
        borderColor: '#64A8D2',
        extendedProps: {
          description: (() => {
            try {
              return typeof ev.content === 'string' && ev.content.trim().startsWith('[')
                ? JSON.parse(ev.content)
                : ev.content || 'No description';
            } catch { return ev.content || 'No description'; }
          })(),
          totalSteps,
          current,
          completed,
          sche_id: ev.idx ?? null,
          user_id: ev.user_id ?? null,
          date: ev.date ?? null,
          playtime: ev.playtime ?? null,
          updated_at: ev.updated_at ?? null,
          raw_content: ev.content,
          raw_gameMode: ev.gameMode,
        },
      };
    });

    setEvents(fetched);
  } catch (err) {
    if (err?.response?.status === 404) {
      setEvents([]);
      return;
    }
    console.error('fetchSchedules failed:', err);
    alert('서버 오류가 발생했습니다.');
  }
}, []);

  useEffect(() => { fetchEventsFromServer(); }, [fetchEventsFromServer]);

  const handleDateClick = (arg) => {
    setSelectedDate(arg.dateStr);
    setIsOpen(true);
  };

  // 상세 조회
  const fetchEventDetail = useCallback(async ({ sche_id, date, user_id }) => {
    try {
      setDetailLoading(true);
      const { data: json } = await api.post('/manager/content/selectsche/detail', {
        sche_id, date, user_id, // 서버가 JWT로 clinic_id 확인
      });

      if (json?.data?.status) {
        const row = json.data.detail || json.data;
        const st = computeStatus(row);
        return {
          description: row.content || 'No description',
          playtimeSec: row.playtime ?? null,
          completed: st.completed,
          dropout: st.dropout,
          totalSteps: st.totalSteps,
          current: st.current,
          updated_at: row.updated_at ?? null,
        };
      }
    } catch (e) {
      console.error('fetchEventDetail error:', e);
    } finally {
      setDetailLoading(false);
    }
    return null;
  }, []);

  const handleEventClick = async (clickInfo) => {
    const ev = clickInfo.event;
    const x = ev.extendedProps || {};

    const seed = {
      id: ev.id,
      title: ev.title,
      dateStr: ev.startStr,
      description: x.description,
      playtimeSec: x.playtime ?? null,
      completed: x.completed ?? null,
      dropout: x.dropout ?? null,
      totalSteps: x.totalSteps ?? null,
      current: x.current ?? null,
      updated_at: x.updated_at ?? null,
    };

    setSelectedEvent(seed);
    setEventOpen(true);

    if (seed.playtimeSec == null || seed.updated_at == null || seed.dropout == null) {
      const more = await fetchEventDetail({
        sche_id: x.sche_id,
        date: x.date || ev.startStr,
        user_id: x.user_id,
      });
      if (more) setSelectedEvent((prev) => ({ ...prev, ...more }));
    }
  };

  const closeModal = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsOpen(false);
    setEventOpen(false);
    setEventTitle('');
    setSelectedEvent({});
    setDetailLoading(false);
  };

  const handleAddEvent = () => {
    const newEvent = {
      title: eventTitle,
      start: selectedDate,
      backgroundColor: '#64A8D2',
      borderColor: '#64A8D2',
      extendedProps: {
        description: 'Manual entry',
        totalSteps: 0,
        current: 0,
        completed: false,
        dropout: false,
        playtime: null,
      },
    };
    setEvents((prev) => [...prev, newEvent]);
    closeModal();
  };

  return (
    <Menu>
      <Box sx={{
        float: 'left', width: 'calc(100% - 260px)', minWidth: '98%',
        height: '85vh', marginTop: '30px', marginLeft: '10px',
        overflowX: 'auto', whiteSpace: 'nowrap', padding: '10px 2px',
      }}>
        <FullCalendar
          plugins={[interactionPlugin, dayGridPlugin]}
          initialView="dayGridMonth"
          selectable
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          ref={calendarRef}
          events={events}
          height="100%"
          width="100%"
        />

        {/* 날짜 선택 모달 */}
        <Modal isOpen={isOpen} onRequestClose={closeModal} style={customStyles} contentLabel="Add Event">
          <h2>Selected Date: {selectedDate}</h2>
          <input
            type="text"
            placeholder="Enter event title"
            value={eventTitle}
            onChange={(e) => setEventTitle(e.target.value)}
            style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
          />
          <button onClick={handleAddEvent}>Add Event</button>
          <button onClick={closeModal}>Close</button>
        </Modal>

        {/* 이벤트 상세 모달 */}
        <Modal isOpen={eventOpen} onRequestClose={closeModal} style={customStyles} contentLabel="Event Details">
          <h2 style={{ marginBottom: 8 }}>{selectedEvent.title}</h2>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 10 }}>{selectedEvent.dateStr}</div>
          <p style={{ marginBottom: 12 }}>{selectedEvent.description}</p>

          <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
            <div><b>수행 시간</b>: {fmtDuration(selectedEvent.playtimeSec)}</div>
            <div>
              <b>완료 여부</b>:{' '}
              {selectedEvent.completed == null ? '—' : (selectedEvent.completed ? '완료' : '미완료')}
              {selectedEvent.totalSteps != null && selectedEvent.current != null
                ? `  (진행 ${selectedEvent.current}/${selectedEvent.totalSteps})`
                : null}
            </div>
            <div><b>중도 이탈</b>:{' '}
              {selectedEvent.dropout == null ? '—' : (selectedEvent.dropout ? '예' : '아니오')}
            </div>
            {selectedEvent.updated_at && (
              <div style={{ fontSize: 12, color: '#888' }}>
                마지막 진행: {new Date(selectedEvent.updated_at).toLocaleString()}
              </div>
            )}
          </div>

          {detailLoading && <div style={{ fontSize: 12, color: '#888' }}>세부 정보 불러오는 중…</div>}

          <button onClick={closeModal}>Close</button>
        </Modal>
      </Box>
    </Menu>
  );
}

export default Calendar;
